import { NavLink, useLocation } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import { Tooltip } from '@/shared/components/Tooltip'
import { useSidebar } from '@/context/SidebarContext'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { OrgWorkspaceSwitcher } from '@/features/org/components/OrgWorkspaceSwitcher'
import { ToastContainer } from '@/shared/components/ToastContainer'
import { ProfileMenu } from '@/shell/ProfileMenu'
import { SearchBox } from '@/shell/SearchBox'
import { AdminButton } from '@/shell/components/AdminButton'
import { GlobalPublishButton } from '@/shell/components/GlobalPublishButton'
import { ThemeToggle } from '@/shell/components/ThemeToggle'
import { styles } from '@/shell/layout.styles'
import { NavSkeletons } from '@/shell/nav/NavSkeletons'
import { SidebarNavItem } from '@/shell/nav/SidebarNavItem'
import { NAV_ICONS, type NavItem } from '@/shell/nav/navIcons'
import { NAV_DASH } from '@/shell/nav/navTables'
import { AuditNavButton } from '@/shell/promos/AuditPromo'
import { BetaEnterpriseUpgradeButton } from '@/shell/promos/BetaEnterpriseUpgrade'
import { BetaFeedbackCard } from '@/shell/promos/BetaFeedbackCard'
import { CoderTemplatesNavButton } from '@/shell/promos/CoderTemplatesPromo'
import { DemoDataButton } from '@/shell/promos/DemoJoinSuccess'
import { DriftDetectionNavButton } from '@/shell/promos/DriftDetectionPromo'
import { McpServerNavButton } from '@/shell/promos/McpServerPromo'
import { PlansNavButton } from '@/shell/promos/PlansIntroPromo'
import { type Props } from '@/shell/types'

export function Layout({ children, title, fillHeight = false }: Props) {
  const { selectedOrg, orgsLoading } = useSelectedOrg()
  const { collapsed, toggle } = useSidebar()
  const { pathname } = useLocation()
  const [actionsOpen, setActionsOpen] = useState(false)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)

  // Escape closes the topbar-actions menu and returns focus to the burger.
  useEffect(() => {
    if (!actionsOpen) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setActionsOpen(false)
        toggleRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [actionsOpen])

  // Click-outside closes (mirrors the OrgWorkspaceSwitcher / ProfileMenu pattern).
  useEffect(() => {
    if (!actionsOpen) return
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node
      if (toggleRef.current?.contains(target)) return
      if (actionsRef.current?.contains(target)) return
      setActionsOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [actionsOpen])

  // Close when the viewport grows back to desktop so the menu can't strand open.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1025px)')
    function onChange(e: MediaQueryListEvent) {
      if (e.matches) setActionsOpen(false)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Belt-and-braces: close on any route change (actions can navigate).
  useEffect(() => {
    setActionsOpen(false)
  }, [pathname])

  const orgBase = selectedOrg ? `/org/${selectedOrg.slug}` : ''

  // Plans is rendered via <PlansNavButton> (a "coming soon" interest modal, not a direct link -
  // see its own comment) rather than a plain NavLink, so it isn't in this list.
  const NAV_ARTEFACTS: NavItem[] = selectedOrg ? [
    { to: `${orgBase}/modules`, label: 'Modules', icon: 'modules', matchPrefix: `${orgBase}/modules`, tooltip: 'Browse, publish, and manage versioned Terraform modules' },
    { to: `${orgBase}/state`, label: 'State Files', icon: 'state', matchPrefix: `${orgBase}/state`, tooltip: 'Terraform HTTP state backend - store and lock state files per workspace' },
  ] : []

  const NAV_ARTEFACTS_AFTER_PLANS: NavItem[] = selectedOrg ? [
    { to: `${orgBase}/providers`, label: 'Providers', icon: 'providers', matchPrefix: `${orgBase}/providers`, tooltip: 'Serve upstream and terramantle-built providers; scan for CVEs, malware & SBOM' },
  ] : []

  const NAV_GOVERNANCE: NavItem[] = selectedOrg ? [
    { to: `${orgBase}/policies`, label: 'Policies', icon: 'policies', matchPrefix: `${orgBase}/policies`, tooltip: 'OPA / Rego policies that gate module and plan publishing across workspaces' },
    { to: `${orgBase}/graph`, label: 'Dependency Graph', icon: 'graph', matchPrefix: `${orgBase}/graph`, tooltip: 'Visualise dependency graph of modules and their relationships' },
    { to: `${orgBase}/tokens`, label: 'Bot Tokens', icon: 'tokens', matchPrefix: `${orgBase}/tokens`, tooltip: 'Machine client tokens for CI pipelines and API access for when Pipeline trust isn\'t available' },
  ] : []

  return (
    <div style={styles.root}>
      {/* ── Sidebar ── */}
      <aside style={{ ...styles.sidebar, width: collapsed ? 64 : 280, minWidth: collapsed ? 64 : 280 }}>
        {/* Logo */}
        <div style={{ ...styles.logoArea, ...(collapsed ? { padding: '18px 0 14px' } : {}) }}>
          <div style={{ ...styles.logoRow, ...(collapsed ? { justifyContent: 'center' } : {}) }}>
            <NavLink to="/" style={styles.logo} aria-label="Terramantle home">
              <div style={styles.logoMark}>T</div>
              {!collapsed && <span>Terra<em style={{ color: 'var(--accent)', fontStyle: 'normal' }}>mantle</em></span>}
            </NavLink>
          </div>
        </div>

        {/* Org workspace switcher */}
        <OrgWorkspaceSwitcher />

        {/* Nav */}
        <nav style={styles.nav}>
          {orgsLoading && !selectedOrg ? (
            <>
              <NavSkeletons count={NAV_DASH.length} />
              <NavSkeletons count={3} />
              <NavSkeletons count={NAV_GOVERNANCE.length} />
            </>
          ) : (
            <>
              <div style={styles.navSection}>
                {!collapsed && <div style={styles.navLabel}>// home</div>}
                {NAV_DASH.map((item) => <SidebarNavItem key={item.to} item={item} />)}
              </div>

              {NAV_ARTEFACTS.length > 0 && (
                <>
                  {collapsed && <div style={styles.navDivider} />}
                  <div style={styles.navSection}>
                    {!collapsed && <div style={styles.navLabel}>// artefacts</div>}
                    {NAV_ARTEFACTS.map((item) => <SidebarNavItem key={item.to} item={item} />)}
                    <PlansNavButton orgBase={orgBase} />
                    {NAV_ARTEFACTS_AFTER_PLANS.map((item) => <SidebarNavItem key={item.to} item={item} />)}
                    {collapsed
                      ? <div style={styles.navDividerSubtle} />
                      : <div style={styles.navLabel}>// integrations</div>}
                    <CoderTemplatesNavButton />
                    <McpServerNavButton />
                  </div>
                </>
              )}

              {collapsed && <div style={styles.navDivider} />}
              <div style={styles.navSection}>
                {!collapsed && <div style={styles.navLabel}>// governance</div>}
                {NAV_GOVERNANCE.map((item) => <SidebarNavItem key={item.to} item={item} />)}
                <DriftDetectionNavButton />
                <AuditNavButton />
              </div>

            </>
          )}
        </nav>

        {/* Collapse toggle - pinned above the feedback card (desktop-only; hidden in the mobile drawer) */}
        <div className="app-collapse-row" style={{ ...styles.collapseRow, ...(collapsed ? { justifyContent: 'center' } : {}) }}>
          <Tooltip text={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <button
              style={styles.themeToggle}
              onClick={toggle}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
            >
              <span style={{ display: 'inline-flex', transform: collapsed ? undefined : 'rotate(180deg)', transition: 'transform 0.18s' }}>
                {NAV_ICONS.chevron}
              </span>
            </button>
          </Tooltip>
        </div>

        {/* Feedback - pinned to sidebar bottom */}
        <BetaFeedbackCard />
      </aside>

      {/* ── Main ── */}
      <main style={styles.main}>
        {/* Topbar - always rendered */}
        <header className="app-topbar" style={styles.topbar}>
          {/* topbarTitle is a flex row so a trailing status badge (e.g. the
              amber "Experimental" pill some pages pass as part of `title`)
              stays vertically centred with the title text. */}
          <h1 style={styles.topbarTitle}>{title ?? ''}</h1>
          {/* On mobile the burger reveals the actions as a dropdown; on desktop
              the burger is hidden and the actions render inline. */}
          <div className="app-topbar-right" style={styles.topbarRight}>
            <button
              ref={toggleRef}
              className="app-nav-toggle"
              aria-haspopup="menu"
              aria-expanded={actionsOpen}
              aria-controls="app-topbar-actions"
              aria-label={actionsOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setActionsOpen((o) => !o)}
            >
              <span className="app-nav-toggle-bars" aria-hidden="true" />
            </button>
            <div
              id="app-topbar-actions"
              ref={actionsRef}
              className={`app-topbar-actions${actionsOpen ? ' app-topbar-actions--open' : ''}`}
              style={styles.topbarActions}
            >
              <DemoDataButton />
              <BetaEnterpriseUpgradeButton />
              <SearchBox />
              <GlobalPublishButton />
              <ThemeToggle />
              <AdminButton />
              <ProfileMenu />
            </div>
          </div>
        </header>

        {/* Content */}
        <ToastContainer />
        <div
          className="app-content"
          style={fillHeight ? { ...styles.content, ...styles.contentFill } : styles.content}
        >
          {children}
        </div>
      </main>
    </div>
  )
}

